import { useState, useEffect } from 'react';
import axios from 'axios';
import { useFormContext } from 'react-hook-form';
import { QueryKeyConfig, getData } from '../api/api-helper';

interface useDebouncedValidationProps {
  name: string;
  validationQueryRoute?: (value: any) => QueryKeyConfig;
  debounceDelay?: number;
}

const useDebouncedValidation = ({
  name,
  validationQueryRoute,
  debounceDelay = 300,
}: useDebouncedValidationProps) => {
  const [value, setValue] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);

  const {
    setError,
    clearErrors,
    getFieldState,
  } = useFormContext();

  useEffect(() => {
    if (!validationQueryRoute) return;
    if (!value || value.trim() === '') return;

    const source = axios.CancelToken.source();

    const debounceTimer = setTimeout(async () => {
      try {
        const query = validationQueryRoute(value);

        if (!query || query.isEnabled === false) return;

        setIsLoading(true);

        const response = await getData<any>(
          query.route,
          query.params,
          { cancelToken: source.token }
        );

        const fieldState = getFieldState(name);

        // 🔴 If API returns error
        if (response.message && response.message !== '') {

          // Do NOT override manual error
          if (fieldState.error?.type !== 'manual') {
            setError(name, {
              type: 'server',
              message: response.message,
            });
          }

        } else {
          // 🟢 API success

          // Only clear if NOT manual error
          if (fieldState.error?.type !== 'manual') {
            clearErrors(name);
          }
        }

      } catch (err: any) {
        if (!axios.isCancel(err)) {
          const errorMessage =
            err?.response?.data?.message ||
            'Error occurred while validating';

          setError(name, {
            type: 'server',
            message: errorMessage,
          });
        }
      } finally {
        setIsLoading(false);
      }
    }, debounceDelay);

    return () => {
      clearTimeout(debounceTimer);
      source.cancel();
    };
  }, [value, debounceDelay]); // ✅ NO errors dependency

  const validate = (newValue: any) => {
    setValue(newValue);
  };

  return { validate, isLoading };
};

export default useDebouncedValidation;